import { toRaw } from "vue";
import { kebabCase } from "@nwt/norce-pwa/stringManipulation";
import type { Category } from "@nwt/norce-pwa/dist/runtime/types/api/category/category";
import type { BreadcrumbsCategory } from "~/types/composables/useCategoryTypes";

type CategoryTreeIndex = {
	byCategoryId: Map<number, Category>;
	byId: Map<number, Category>;
	/** Filled lazily: a page with a handful of links slugs a handful of nodes. */
	paths: Map<Category, string>;
};

/*
	One index per category tree, keyed on the raw tree array (NF-390).

	Every rendered category link used to walk the reactive tree depth-first and
	deep-copy the matched node's whole subtree, then do the same for each parent.
	The gotaplatsensfoto homepage renders 331 links, on the server and again
	during hydration, which held the hero image unpainted for ~3 s on a phone.
	Here the tree is walked once and each node is slugged at most once.

	Not a store getter: gotaplatsensfoto forks the category store, so a getter
	added to the base store would be missing on exactly the template it is for.

	Shared across requests on the server, which is safe: the key is the
	request's own tree object, and every value is derived from that key alone
	(immutable strings and references into that same tree), so no render can see
	another's data, and the entry goes when the tree is garbage collected.

	It relies on the tree being replaced, never edited in place. That is how
	every update arrives today: the server parses fresh JSON per request and
	`setCategoryTree` swaps the object on the client. Mutating a node or a
	`Children` array would leave this index answering with the old paths.
*/
const indexes = new WeakMap<Category[], CategoryTreeIndex>();

const buildIndex = (tree: Category[]): CategoryTreeIndex => {
	const byCategoryId = new Map<number, Category>();
	const byId = new Map<number, Category>();
	const visit = (nodes: Category[]) => {
		for (const node of nodes) {
			// First match wins, like the depth-first search this replaces.
			if (!byCategoryId.has(node.CategoryId)) {
				byCategoryId.set(node.CategoryId, node);
			}
			if (!byId.has(node.Id)) byId.set(node.Id, node);
			if (node.Children?.length) visit(node.Children);
		}
	};
	visit(tree);
	return { byCategoryId, byId, paths: new Map() };
};

/**
 * The index for this tree, built on first use. Walks `toRaw(tree)` so the walk
 * hits no proxy traps and records no reactive dependencies; callers read the
 * tree through the store first, which is what keeps them reactive.
 */
const indexFor = (tree: Category[]): CategoryTreeIndex => {
	const raw = toRaw(tree);
	const existing = indexes.get(raw);
	if (existing) return existing;
	const index = buildIndex(raw);
	indexes.set(raw, index);
	return index;
};

// Parents are found by ParentId -> Id, not by position in the tree, exactly as
// before. Id and CategoryId are different numbers.
const parentOf = (
	index: CategoryTreeIndex,
	node: Category
): Category | undefined =>
	node.ParentId ? index.byId.get(node.ParentId) : undefined;

const pathOf = (index: CategoryTreeIndex, node: Category): string => {
	const known = index.paths.get(node);
	if (known !== undefined) return known;

	// Climb to the root or to the first ancestor whose path is already known.
	const climbed: Category[] = [];
	const seen = new Set<Category>();
	let current: Category | undefined = node;
	let prefix: string | undefined;
	while (current && !seen.has(current)) {
		prefix = index.paths.get(current);
		if (prefix !== undefined) break;
		seen.add(current);
		climbed.push(current);
		current = parentOf(index, current);
	}
	// Stopping on a node already climbed means the ParentId chain loops: bad
	// data, which the old recursion never returned from. That answer depends on
	// where the climb started, so it is not cached; caching it would make links
	// depend on render order, and SSR and hydration could disagree.
	const isCycle = current !== undefined && prefix === undefined;

	// Back down from the top, so each ancestor's path is the prefix of the next.
	let path = prefix;
	for (let i = climbed.length - 1; i >= 0; i--) {
		const category = climbed[i]!;
		const slug = kebabCase(category.Name);
		path = path === undefined ? slug : `${path}/${slug}`;
		if (!isCycle) index.paths.set(category, path);
	}
	return path ?? "";
};

/**
 * The `parent/child` slug path of a category, as stored in the `urls` index,
 * or `""` when the tree is not loaded or cannot place the id.
 */
export const resolveCategoryPath = (
	tree: Category[] | null | undefined,
	categoryId: number
): string => {
	if (!tree?.length) return "";
	const index = indexFor(tree);
	const node = index.byCategoryId.get(categoryId);
	return node ? pathOf(index, node) : "";
};

/**
 * The first node, in depth-first order, whose `key` equals `id`, or `null`.
 * The node belongs to the tree: copy it before handing it to a store.
 */
export const findCategoryNode = (
	tree: Category[] | null | undefined,
	id: number,
	key: "CategoryId" | "Id"
): Category | null => {
	if (!tree?.length) return null;
	const index = indexFor(tree);
	const lookup = key === "Id" ? index.byId : index.byCategoryId;
	return lookup.get(id) ?? null;
};

/**
 * A fresh `{ Name, Code, Parent }` chain from the category's parent up to the
 * root, or `null` for a category with no parent in the tree. Built anew on
 * every call because it ends up in Pinia, where consumers may clone or mutate it.
 */
export const buildParentChain = (
	tree: Category[],
	category: Category
): Partial<BreadcrumbsCategory> | null => {
	const index = indexFor(tree);
	const node = toRaw(category);
	const seen = new Set<Category>([node]);
	const ancestors: Category[] = [];
	let parent = parentOf(index, node);
	while (parent && !seen.has(parent)) {
		seen.add(parent);
		ancestors.push(parent);
		parent = parentOf(index, parent);
	}
	return ancestors.reduceRight<Partial<BreadcrumbsCategory> | null>(
		(chain, ancestor) => ({
			Name: ancestor.Name,
			Code: ancestor.Code,
			Parent: chain
		}),
		null
	);
};
